import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Modal, Button, message, Typography, Card } from 'antd';
import Lottie from 'lottie-react';
import successAnimation from '../components/lottie/success.json';
import { useAuth } from './AuthContext';
import { db, storage } from '../firebaseConfig';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { collection, getDocs, doc, updateDoc, Timestamp } from 'firebase/firestore';

// PNG с канвы приходит как dataURL — переводим в Blob, иначе в Storage не загрузить.
const dataUrlToBlob = (dataUrl) => {
    const byteCharacters = atob(dataUrl.split(',')[1]);
    const byteArrays = [];
    for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);
        const byteNumbers = Array.from(slice, char => char.charCodeAt(0));
        byteArrays.push(new Uint8Array(byteNumbers));
    }
    return new Blob(byteArrays, { type: 'image/png' });
};

// Модалка с канвой подписи вынесена из легаси-флоу отдельно, чтобы тот же
// код (поворот в альбомную, рисование пальцем/мышью, очистка, защита от
// пустой подписи) использовали и стартовые документы, и umowy на странице
// Dokumenty. Родитель получает готовый PNG-Blob через onSave и сам решает,
// куда его грузить и что писать в базу — модалка о хранилище ничего не знает.
export const SignatureModal = ({ open, title, onCancel, onSave }) => {
    const [isPortrait, setIsPortrait] = useState(window.matchMedia('(orientation: portrait)').matches);
    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const [drawing, setDrawing] = useState(false);
    const [saving, setSaving] = useState(false);
    // Снимок пустой канвы: сравнением dataURL отсекаем «подпись», в которой никто не рисовал.
    const [emptySignatureData, setEmptySignatureData] = useState('');

    const initializeCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            canvas.width = canvas.clientWidth;
            canvas.height = canvas.clientHeight;
            const ctx = canvas.getContext('2d');
            if (ctx) {
                ctx.strokeStyle = 'black';
                ctx.lineWidth = 2;
                ctx.lineCap = 'round';
                ctxRef.current = ctx;
                setEmptySignatureData(canvas.toDataURL());
            }
        }
    }, []);

    useEffect(() => {
        const handleOrientationChange = (e) => {
            setIsPortrait(e.matches);
            if (!e.matches) {
                // Канва появляется только в альбомной ориентации — даём модалке перерисоваться.
                setTimeout(initializeCanvas, 100);
            }
        };

        const portraitMediaQuery = window.matchMedia('(orientation: portrait)');
        portraitMediaQuery.addEventListener('change', handleOrientationChange);

        return () => {
            portraitMediaQuery.removeEventListener('change', handleOrientationChange);
        };
    }, [initializeCanvas]);

    useEffect(() => {
        if (open && !isPortrait) {
            // Как и в легаси-флоу: ждём, пока Modal смонтирует содержимое, иначе clientWidth = 0.
            setTimeout(initializeCanvas, 100);
        }
    }, [open, isPortrait, initializeCanvas]);

    const getCanvasCoordinates = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        if (event.touches && event.touches.length > 0) {
            return {
                x: (event.touches[0].clientX - rect.left) * scaleX,
                y: (event.touches[0].clientY - rect.top) * scaleY,
            };
        } else {
            return {
                x: (event.clientX - rect.left) * scaleX,
                y: (event.clientY - rect.top) * scaleY,
            };
        }
    };

    const startDrawing = (e) => {
        e.preventDefault();
        e.stopPropagation(); // Останавливаем распространение события
        document.body.classList.add('disable-scroll'); // Отключаем прокрутку страницы

        const ctx = ctxRef.current;
        if (ctx) {
            setDrawing(true);
            const { x, y } = getCanvasCoordinates(e);
            ctx.beginPath();
            ctx.moveTo(x, y);
        }
    };

    const draw = (e) => {
        e.preventDefault();
        e.stopPropagation(); // Останавливаем распространение события

        if (!drawing) return;
        const ctx = ctxRef.current;
        if (ctx) {
            const { x, y } = getCanvasCoordinates(e);
            ctx.lineTo(x, y);
            ctx.stroke();
        }
    };

    const stopDrawing = () => {
        setDrawing(false);
        document.body.classList.remove('disable-scroll'); // Включаем прокрутку страницы
    };

    const clearSignature = () => {
        const ctx = ctxRef.current;
        if (ctx && canvasRef.current) {
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        }
    };

    const handleSave = async () => {
        if (!canvasRef.current) return;
        const signatureData = canvasRef.current.toDataURL();
        if (signatureData === emptySignatureData) {
            message.error('Nie wykryto podpisu. Podpisz dokument przed zapisaniem.');
            return;
        }

        setSaving(true);
        try {
            // Родитель грузит PNG и пишет запись о подписи; ошибки тоже обрабатывает он —
            // модалка лишь не даёт нажать «Zapisz» дважды, пока запись идёт.
            await onSave(dataUrlToBlob(signatureData));
            clearSignature();
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            onCancel={onCancel}
            footer={null}
            maskClosable={false}
        >
            {isPortrait ? (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                    <Typography.Title level={4}>Proszę obrócić urządzenie w poziome położenie, aby podpisać dokument.</Typography.Title>
                </div>
            ) : (
                <>
                    <Typography.Title level={4} style={{ textAlign: 'center' }}>
                        {title}
                    </Typography.Title>
                    <div style={{ border: '1px solid #ccc', padding: '10px', borderRadius: '5px', marginTop: '20px', overflow: 'hidden' }}>
                        <canvas
                            ref={canvasRef}
                            style={{
                                width: '100%',
                                height: '200px',
                                backgroundColor: '#f9f9f9',
                                cursor: 'crosshair',
                                overflow: 'hidden',
                                touchAction: 'none' // предотвращаем нежелательные действия на мобильных устройствах
                            }}
                            onMouseDown={startDrawing}
                            onMouseMove={draw}
                            onMouseUp={stopDrawing}
                            onTouchStart={startDrawing}
                            onTouchMove={draw}
                            onTouchEnd={stopDrawing}
                        />
                    </div>
                    <div style={{ textAlign: 'center', marginTop: '10px' }}>
                        <Button onClick={clearSignature} style={{ marginRight: '10px' }} disabled={saving}>
                            Wyczyść podpis
                        </Button>
                        <Button type="primary" onClick={handleSave} loading={saving}>
                            Zapisz podpis
                        </Button>
                    </div>
                </>
            )}
        </Modal>
    );
};

const DocumentSigning = ({ visible, onClose }) => {
    const { currentUser } = useAuth();
    const [documents, setDocuments] = useState([]);
    const [isSignatureModalVisible, setIsSignatureModalVisible] = useState(false);
    const [isDocumentModalVisible, setIsDocumentModalVisible] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [allDocumentsSigned, setAllDocumentsSigned] = useState(false);
    const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false); // Модальное окно с успехом
    const [currentContractId, setCurrentContractId] = useState(null);

    const fetchDocuments = useCallback(async () => {
        if (currentUser) {
            const docsSnapshot = await getDocs(collection(db, `users/${currentUser.uid}/umowy`));
            const docs = docsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setDocuments(docs);

            const allSigned = docs.every(doc => doc.signed);
            setAllDocumentsSigned(allSigned);
            return allSigned;
        }
        return false;
    }, [currentUser]);

    useEffect(() => {
        fetchDocuments();
    }, [fetchDocuments]);

    // Легаси-флоу стартовых документов: PNG в Storage, отметка о подписи —
    // в Firestore users/{uid}/umowy. Не путать с новыми umowami в RTDB.
    const handleSaveSignature = async (blob) => {
        const signatureRef = ref(storage, `signatures/${currentUser.uid}/${currentContractId}.png`);

        try {
            await uploadBytesResumable(signatureRef, blob);
            const signatureURL = await getDownloadURL(signatureRef);
            const signedAt = Timestamp.now();

            await updateDoc(doc(db, `users/${currentUser.uid}/umowy`, currentContractId), {
                signature: signatureURL,
                signed: true,
                signedAt: signedAt,
            });

            message.success('Dokument został pomyślnie podpisany.');
            setIsSignatureModalVisible(false);

            const allSigned = await fetchDocuments();
            if (allSigned) {
                setIsSuccessModalVisible(true); // Показать модальное окно с успехом
            }
        } catch (error) {
            message.error('Nie udało się zapisać podpisu. Spróbuj ponownie.');
            console.error('Error saving signature:', error);
        }
    };

    const openSignatureModal = (docId) => {
        setCurrentContractId(docId);
        setIsSignatureModalVisible(true);
    };

    const openDocumentModal = (doc) => {
        setSelectedDocument(doc);
        setIsDocumentModalVisible(true);
    };

    const closeDocumentModal = () => {
        setIsDocumentModalVisible(false);
        setSelectedDocument(null);
    };

    return (
        <>
            <Modal
                visible={visible}
                onCancel={onClose}
                footer={null}
                maskClosable={false}
            >
                <div id="documents-container">
                    {documents.filter(doc => !doc.signed).map(doc => (
                        <Card
                            key={doc.id}
                            title={`Dokument: ${doc.id}`}
                            extra={<Button onClick={() => openDocumentModal(doc)}>Zobacz dokument</Button>}
                            style={{ marginBottom: '20px' }}
                        >
                            <p><span className="detail-label">Wygasa:</span> {new Date(doc.dataGeneracji.seconds * 1000).toLocaleString()}</p>
                            <p><span className="detail-label">Status:</span> "Niepodpisany"</p>
                            <Button type="primary" onClick={() => openSignatureModal(doc.id)} style={{ marginTop: '10px' }}>
                                Podpisz dokument
                            </Button>
                        </Card>
                    ))}
                </div>
                <div style={{ textAlign: 'center', marginTop: '10px' }}>
                    <Button
                        type="primary"
                        onClick={onClose}
                        disabled={!allDocumentsSigned}
                    >
                        Zakończ
                    </Button>
                </div>
            </Modal>

            <SignatureModal
                open={isSignatureModalVisible}
                title={`Podpisywanie dokumentu: ${currentContractId}`}
                onCancel={() => setIsSignatureModalVisible(false)}
                onSave={handleSaveSignature}
            />

            <Modal
                visible={isDocumentModalVisible}
                onCancel={closeDocumentModal}
                footer={[
                    <Button key="close" onClick={closeDocumentModal}>Zamknij</Button>,
                    <Button key="sign" type="primary" onClick={() => openSignatureModal(selectedDocument.id)}>Podpisz</Button>
                ]}
            >
                {selectedDocument && (
                    <div>
                        <Typography.Title level={4}>Dokument: {selectedDocument.id}</Typography.Title>
                        <p><span className="detail-label">Wygasa:</span> {new Date(selectedDocument.dataGeneracji.seconds * 1000).toLocaleString()}</p>
                        <p><span className="detail-label">Status:</span> "Niepodpisany"</p>
                        <iframe
                            src={selectedDocument.pdfUrl}
                            title="Dokument PDF"
                            style={{ width: '100%', height: '500px', border: 'none' }}
                        />
                    </div>
                )}
            </Modal>

            <Modal
                visible={isSuccessModalVisible} // Модальное окно успеха
                onCancel={onClose}
                footer={null}
                maskClosable={false}
            >
                <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Lottie animationData={successAnimation} loop={false} autoplay style={{ width: '200px', height: '200px', margin: '0 auto' }} />
                    <Typography.Title level={4} style={{ marginTop: '20px' }}>
                        Wszystkie dokumenty zostały pomyślnie podpisane! Możesz teraz korzystać z aplikacji.
                    </Typography.Title>
                    <Button type="primary" onClick={onClose} style={{ marginTop: '20px' }}>
                        OK
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export default DocumentSigning;
